import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import sentryInit from '../sentry';
import { WidgetConfigResponse } from 'rwl-shared/dist/models';

export type BumpOutTest = {
    openPanel: 'left' | 'right';
    tabLocation: 'top' | 'middle' | 'bottom';
    requiresPostCode: boolean;
    ctaText: string;
    font: 'inherit' | 'Arial' | 'Helvetica' | 'Times New Roman';
    tabBackgroundColor: string;
    tabTextColor: string;
    animateTab: boolean;
};

export type GlobalConfig = {
    accountId: string;
    apiBaseUrl: string;
    brandColor?: string;
    sentryEnvironment?: string;
    config?: WidgetConfigResponse;
    bumpOutTestEnabled?: boolean;
    bumpOutTest?: BumpOutTest;
    publicUrl?: string;
};

type GlobalContextType = GlobalConfig & {
    isReady: boolean;
    setBrandColor: (brandColor: string) => void;
    setConfig: (config: any) => void;
    setIsReady: (isReady: boolean) => void;
};

const DEFAULT_BRAND_COLOR = '#346db5';
const DEFAULT_SENTRY_ENVIRONMENT = 'local';

/**
 * The type-trickery here is intentional.  We need the context to be undefined until the global state is loaded.
 * However we also need to be able to use the context without checking for undefined everywhere always.
 * We block rendering any children until the global state is loaded, so we can safely assume that the context
 * will be defined by the time we need to use it.
 */
const GlobalContext = createContext<GlobalContextType>(
    undefined as unknown as GlobalContextType
);

export const GlobalProvider = ({
    children,
    ...props
}: PropsWithChildren<GlobalConfig>): JSX.Element | null => {
    const [brandColor, setBrandColor] = useState<string>(
        props.brandColor ?? DEFAULT_BRAND_COLOR
    );
    const [config, setConfig] = useState<WidgetConfigResponse | undefined>(
        props.config
    );
    const [isReady, setIsReady] = useState(false);
    const [sentryEnvironment] = useState(props.sentryEnvironment); // No setter, can only init sentry once

    useEffect(() => {
        sentryInit(
            sentryEnvironment?.length
                ? sentryEnvironment
                : DEFAULT_SENTRY_ENVIRONMENT
        );
    }, []);

    useEffect(() => {
        if (config) {
            setBrandColor(
                config.account.mapSettings.color || DEFAULT_BRAND_COLOR
            );
        }
    }, [config]);

    return (
        <GlobalContext.Provider
            value={{
                accountId: props.accountId,
                apiBaseUrl: props.apiBaseUrl,
                brandColor,
                setBrandColor,
                sentryEnvironment,
                config,
                setConfig,
                isReady,
                bumpOutTestEnabled: props.bumpOutTestEnabled,
                bumpOutTest: props.bumpOutTest,
                setIsReady: (isReady: boolean) => {
                    if (isReady) {
                        console.info('RWL Widget initialized');
                    }
                    setIsReady(isReady);
                },
            }}
        >
            {children}
        </GlobalContext.Provider>
    );
};

export const useGlobal = () => {
    const context = useContext(GlobalContext);
    if (context === undefined) {
        throw new Error('useGlobal must be used within a GlobalProvider');
    }
    return context!;
};

export const WhenGlobalReady = ({ children }: PropsWithChildren<unknown>) => {
    const { isReady } = useGlobal();
    if (!isReady) {
        console.info('Waiting for app to initialize...');
        return null;
    }
    return <>{children}</>;
};
